import {
  schemaDocumentSnapshotQueryOptions,
  schemaQuerySnapshotQueryOptions,
  schemaSingleDocumentSnapshotQueryOptions,
} from '@valian/react-kitchen-sink'

import { collections } from '@/schema'

export const assignmentQuery = (assignmentId: string) =>
  schemaDocumentSnapshotQueryOptions({
    queryKey: ['assignments', assignmentId],
    factory: collections.assignments,
    id: assignmentId,
  })

export const assignmentCostsQuery = (assignmentId: string) =>
  schemaSingleDocumentSnapshotQueryOptions({
    queryKey: ['assignments', assignmentId, 'costs'],
    factory: collections.assignments(assignmentId).costs,
  })

export const assignmentPlansQuery = (assignmentId: string) =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['assignments', assignmentId, 'plans'],
    factory: collections.assignments(assignmentId).plans,
    query: {
      name: `All plans for assignment ${assignmentId}`,
    },
  })

export const assignmentExecutionsQuery = (assignmentId: string) =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['assignments', assignmentId, 'executions'],
    factory: collections.assignments(assignmentId).executions,
    query: {
      name: `All executions for assignment ${assignmentId}`,
    },
  })

export const assignmentDistancesQuery = (assignmentId: string) =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['distances', 'assignment', assignmentId],
    factory: collections.distances,
    query: {
      name: `All distances for assignment ${assignmentId}`,
      where: [['assignmentId', '==', assignmentId]],
    },
  })

export const previousAssignmentQuery = (regionId?: string, timestamp?: Date) =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['assignments', 'previous', regionId, timestamp?.toISOString()],
    factory: collections.assignments,
    enabled: !!regionId && !!timestamp,
    query: {
      name: `Previous assignment for region ${regionId} before ${timestamp?.toISOString()}`,
      where: [
        ['regionId', '==', regionId],
        ['timestamp', '<', timestamp],
      ],
      orderBy: [['timestamp', 'desc']],
      limit: 1,
    },
  })

export const nextAssignmentQuery = (regionId?: string, timestamp?: Date) =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['assignments', 'next', regionId, timestamp?.toISOString()],
    factory: collections.assignments,
    enabled: !!regionId && !!timestamp,
    query: {
      name: `Next assignment for region ${regionId} after ${timestamp?.toISOString()}`,
      where: [
        ['regionId', '==', regionId],
        ['timestamp', '>', timestamp],
      ],
      orderBy: [['timestamp', 'asc']],
      limit: 1,
    },
  })

export const assignmentsListQuery = ({
  regionId,
  assignmentType = 'region',
  limit = 50,
}: {
  regionId?: string
  assignmentType?: 'region' | 'global'
  limit?: number
}) =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['assignments', 'list', assignmentType, regionId, limit],
    factory: collections.assignments,
    query: regionId
      ? {
          name: `Assignments for region ${regionId} (limit ${limit})`,
          where: [
            ['type', '==', assignmentType],
            ['regionId', '==', regionId],
          ],
          orderBy: [['timestamp', 'desc']],
          limit,
        }
      : {
          name: `All ${assignmentType} assignments (limit ${limit})`,
          where: [['type', '==', assignmentType]],
          orderBy: [['timestamp', 'desc']],
          limit,
        },
  })

export const assignmentsByGroupIdQuery = (groupId?: string) =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['assignments', 'byGroupId', groupId],
    factory: collections.assignments,
    enabled: !!groupId,
    query: {
      name: `Regional assignments for group ${groupId}`,
      where: [
        ['groupId', '==', groupId],
        ['type', '==', 'region'],
      ],
      orderBy: [['timestamp', 'desc']],
    },
  })

export const previousGlobalAssignmentQuery = (timestamp?: Date) =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['assignments', 'global', 'previous', timestamp?.toISOString()],
    factory: collections.assignments,
    enabled: !!timestamp,
    query: {
      name: `Previous global assignment before ${timestamp?.toISOString()}`,
      where: [
        ['type', '==', 'global'],
        ['timestamp', '<', timestamp],
      ],
      orderBy: [['timestamp', 'desc']],
      limit: 1,
    },
  })

export const nextGlobalAssignmentQuery = (timestamp?: Date) =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['assignments', 'global', 'next', timestamp?.toISOString()],
    factory: collections.assignments,
    enabled: !!timestamp,
    query: {
      name: `Next global assignment after ${timestamp?.toISOString()}`,
      where: [
        ['type', '==', 'global'],
        ['timestamp', '>', timestamp],
      ],
      orderBy: [['timestamp', 'asc']],
      limit: 1,
    },
  })
